import { useState, useEffect } from 'react';
import { X, Lock, Eye, EyeOff } from 'lucide-react';
import { App } from 'antd';
import { authFetch } from '../services/auth';
import { hashPassword } from '../utils/passwordHash';
import { API_BASE_URL } from '../config';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// 校验密码规则
const validatePassword = (password: string): string => {
  if (!password) return '请输入密码';
  if (password.length < 8) return '密码长度至少为8位';
  if (password.length > 16) return '密码长度不能超过16位';
  if (!/[a-z]/.test(password)) return '密码必须包含小写字母';
  if (!/[A-Z]/.test(password)) return '密码必须包含大写字母';
  if (!/[0-9]/.test(password)) return '密码必须包含数字';
  return '';
};

const validateConfirmPassword = (
  password: string,
  confirmPassword: string,
): string => {
  if (!confirmPassword) return '请确认新密码';
  if (confirmPassword !== password) return '两次输入的密码不一致';
  return '';
};

export default function ChangePasswordModal({
  isOpen,
  onClose,
  onSuccess,
}: ChangePasswordModalProps) {
  const { message } = App.useApp();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 阻止背景页面滚动
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  // 字段级校验错误
  const [fieldErrors, setFieldErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  // 已触碰的字段
  const [touched, setTouched] = useState<{
    currentPassword?: boolean;
    newPassword?: boolean;
    confirmPassword?: boolean;
  }>({});

  if (!isOpen) return null;

  // 实时校验单个字段
  const validateField = (
    field: 'currentPassword' | 'newPassword' | 'confirmPassword',
    value: string,
  ) => {
    let errorMsg = '';
    switch (field) {
      case 'currentPassword':
        if (!value) errorMsg = '请输入当前密码';
        break;
      case 'newPassword':
        errorMsg = validatePassword(value);
        break;
      case 'confirmPassword':
        errorMsg = validateConfirmPassword(newPassword, value);
        break;
    }
    setFieldErrors((prev) => ({ ...prev, [field]: errorMsg }));
    return errorMsg === '';
  };

  // 处理字段变化
  const handleFieldChange = (
    field: 'currentPassword' | 'newPassword' | 'confirmPassword',
    value: string,
  ) => {
    switch (field) {
      case 'currentPassword':
        setCurrentPassword(value);
        break;
      case 'newPassword':
        setNewPassword(value);
        // 如果确认密码已填写，重新校验确认密码
        if (confirmPassword && touched.confirmPassword) {
          validateField('confirmPassword', confirmPassword);
        }
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
    }
    // 如果字段已被触碰，实时校验
    if (touched[field]) {
      validateField(field, value);
    }
  };

  // 处理字段失焦
  const handleFieldBlur = (
    field: 'currentPassword' | 'newPassword' | 'confirmPassword',
  ) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // 校验整个表单
  const validateForm = (): boolean => {
    const currentPasswordError = !currentPassword ? '请输入当前密码' : '';
    const newPasswordError = validatePassword(newPassword);
    const confirmPasswordError = validateConfirmPassword(
      newPassword,
      confirmPassword,
    );

    setFieldErrors({
      currentPassword: currentPasswordError,
      newPassword: newPasswordError,
      confirmPassword: confirmPasswordError,
    });
    setTouched({
      currentPassword: true,
      newPassword: true,
      confirmPassword: true,
    });

    return !currentPasswordError && !newPasswordError && !confirmPasswordError;
  };

  // 重置表单
  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setFieldErrors({});
    setTouched({});
    setError('');
  };

  // 关闭弹窗
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 表单校验
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // 对密码进行客户端哈希处理
      const hashedCurrentPassword = await hashPassword(currentPassword);
      const hashedNewPassword = await hashPassword(newPassword);

      const response = await authFetch(`${API_BASE_URL}/api/auth/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: hashedCurrentPassword,
          newPassword: hashedNewPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '修改密码失败');
      }

      // 修改成功
      message.success('密码修改成功，请重新登录');
      resetForm();
      onSuccess();
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : '修改密码失败，请稍后重试';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onWheel={(e) => e.preventDefault()}
        onTouchMove={(e) => e.preventDefault()}
      />

      {/* 弹窗 */}
      <div className="relative w-full max-w-md bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden animate-scale-in">
        {/* 顶部装饰 */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-emerald-500 to-amber-500" />

        {/* 关闭按钮 */}
        <button
          onClick={handleClose}
          disabled={loading}
          className="absolute top-4 right-4 text-slate-400 hover:text-amber-400 hover:bg-amber-500/20 rounded-lg p-1 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          {/* 标题 */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">修改密码</h2>
            <p className="text-slate-400 text-sm">修改成功后需要重新登录</p>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* 表单 */}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* 当前密码 */}
            <div>
              <label className="block text-slate-300 text-sm mb-2">
                当前密码
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) =>
                    handleFieldChange('currentPassword', e.target.value)
                  }
                  onBlur={() => handleFieldBlur('currentPassword')}
                  placeholder="请输入当前密码"
                  className={`w-full pl-10 pr-12 py-3 bg-slate-700/50 border rounded-lg text-white placeholder-slate-500 focus:outline-none transition-all duration-200 cursor-text
                    ${
                      fieldErrors.currentPassword && touched.currentPassword
                        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        : 'border-slate-600 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20'
                    }`}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 cursor-pointer transition-colors"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {/* 当前密码校验错误提示 */}
              {fieldErrors.currentPassword && touched.currentPassword && (
                <p className="mt-1.5 text-red-400 text-xs flex items-center gap-1 animate-fade-in">
                  <svg
                    className="w-3.5 h-3.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {fieldErrors.currentPassword}
                </p>
              )}
            </div>

            {/* 新密码 */}
            <div>
              <label className="block text-gray-300 text-sm mb-2">新密码</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) =>
                    handleFieldChange('newPassword', e.target.value)
                  }
                  onBlur={() => handleFieldBlur('newPassword')}
                  placeholder="请输入新密码"
                  className={`w-full pl-10 pr-12 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none transition-all duration-200 cursor-text
                    ${
                      fieldErrors.newPassword && touched.newPassword
                        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        : 'border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                    }`}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 cursor-pointer transition-colors"
                >
                  {showNewPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {/* 新密码校验错误提示 */}
              {fieldErrors.newPassword && touched.newPassword && (
                <p className="mt-1.5 text-red-400 text-xs flex items-center gap-1 animate-fade-in">
                  <svg
                    className="w-3.5 h-3.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {fieldErrors.newPassword}
                </p>
              )}
              {/* 密码规则提示 */}
              <p className="mt-1.5 text-slate-500 text-xs">
                密码长度8-16位，需包含大小写字母和数字
              </p>
            </div>

            {/* 确认新密码 */}
            <div>
              <label className="block text-slate-300 text-sm mb-2">
                确认新密码
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) =>
                    handleFieldChange('confirmPassword', e.target.value)
                  }
                  onBlur={() => handleFieldBlur('confirmPassword')}
                  placeholder="请再次输入新密码"
                  className={`w-full pl-10 pr-12 py-3 bg-slate-700/50 border rounded-lg text-white placeholder-slate-500 focus:outline-none transition-all duration-200 cursor-text
                    ${
                      fieldErrors.confirmPassword && touched.confirmPassword
                        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        : 'border-slate-600 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20'
                    }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 cursor-pointer transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {/* 确认密码校验错误提示 */}
              {fieldErrors.confirmPassword && touched.confirmPassword && (
                <p className="mt-1.5 text-red-400 text-xs flex items-center gap-1 animate-fade-in">
                  <svg
                    className="w-3.5 h-3.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {fieldErrors.confirmPassword}
                </p>
              )}
            </div>

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500
                text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 cursor-pointer"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  修改中...
                </span>
              ) : (
                '确认修改'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* 动画样式 */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }

        /* 覆盖浏览器自动填充样式 */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px rgba(55, 65, 81, 0.5) inset !important;
          -webkit-text-fill-color: white !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>
    </div>
  );
}
